import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';

export default function Cart() {
  const { items, totalPrice, updateQty, removeItem } = useCart();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-full bg-slate-100 mx-auto flex items-center justify-center">
          <ShoppingBag className="w-10 h-10 text-[#5d728c]" />
        </div>
        <h1 className="font-serif text-3xl text-slate-900 mt-6">Your cart is empty</h1>
        <p className="text-slate-600 mt-2">Discover quality office supplies to keep your workspace productive.</p>
        <Button asChild className="mt-8 bg-[#5d728c] hover:bg-[#4d6278] rounded-full px-7">
          <Link to="/shop-all">Browse Products</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.id} className="flex gap-4 bg-white border border-slate-200 rounded-lg p-4">
              <Link to={`/show/${item.id}`} className="w-28 h-28 bg-slate-50 rounded shrink-0">
                <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/show/${item.id}`} className="font-serif text-lg text-slate-800 hover:text-[#5d728c] line-clamp-2">
                  {item.name}
                </Link>
                <p className="text-slate-500 text-sm mt-1">${item.price.toFixed(2)}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-slate-300 rounded-full">
                    <button onClick={() => updateQty(item.id, item.qty - 1)} className="px-3 py-1.5 text-slate-600 hover:text-[#5d728c]" aria-label="Decrease">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-3 text-slate-800 font-medium">{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.qty + 1)} className="px-3 py-1.5 text-slate-600 hover:text-[#5d728c]" aria-label="Increase">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button onClick={() => removeItem(item.id)} className="text-slate-500 hover:text-red-600 text-sm flex items-center gap-1">
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-slate-900">${(item.price * item.qty).toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>

        <aside className="bg-slate-50 rounded-lg p-6 h-fit">
          <h3 className="font-serif text-xl text-slate-900">Order Summary</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-600">Subtotal</dt><dd className="text-slate-900 font-medium">${totalPrice.toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-600">Shipping</dt><dd className="text-emerald-700 font-medium">Free</dd></div>
            <div className="flex justify-between border-t border-slate-200 pt-3 mt-3"><dt className="text-base text-slate-900 font-semibold">Total</dt><dd className="text-base text-slate-900 font-semibold">${totalPrice.toFixed(2)}</dd></div>
          </dl>
          <Button asChild className="w-full mt-6 bg-[#5d728c] hover:bg-[#4d6278] rounded-full">
            <Link to="/checkout">Proceed to Checkout</Link>
          </Button>
          <Link to="/shop-all" className="block text-center mt-3 text-sm text-slate-600 hover:text-[#5d728c]">Continue Shopping</Link>
        </aside>
      </div>
    </div>
  );
}
